import React, {
  ChangeEvent,
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import { CreateFormModal } from 'src/feature/CreateFormModal';
import { DirectoryWorkflowPanel } from 'src/feature/DirectoryWorkflowPanel';
import { FormAuditHistoryModal } from 'src/feature/FormAuditHistoryModal';
import { FormWorkflowPanel } from 'src/feature/FormWorkflowPanel';
import { Table } from 'src/feature/Table';
import { TableFiltersModal } from 'src/feature/Table/ui/TableFiltersModal';
import { TableInfo } from 'src/feature/TableInfo';
import { GetFormWorkflowRes } from 'src/shared/api/projects/types';
import { ReactComponent as FilterIcon } from 'src/shared/assets/icons/kit/filter.svg';
import { ReactComponent as InfoCircleIcon } from 'src/shared/assets/icons/kit/info-circle.svg';
import { ReactComponent as LoupeIcon } from 'src/shared/assets/icons/kit/loupe.svg';
import { buildAbsoluteAppUrl } from 'src/shared/config';
import { routesEnum } from 'src/shared/const';
import { useGetForm } from 'src/shared/lib/hooks/useGetForm';
import { debounce, setError, setSuccess, setWarning } from 'src/shared/lib/utils';
import { FormViewSettings } from 'src/shared/store/projects/types';
import { WsFilterCondition } from 'src/shared/store/table/searchFilters';
import { UserContext } from 'src/shared/store/user';
import { canManageModel, canWriteData } from 'src/shared/store/user/permissions';
import { Button, ToolTip } from 'src/shared/ui';
import { Loader } from 'src/shared/ui/Loader';
import { HeadItems } from 'src/widgets/ProjectTable/components/HeadItems';
import { TableButtons } from 'src/widgets/ProjectTable/components/TableButtons';
import { recalculateCurrentFormRules } from 'src/widgets/ProjectTable/lib/recalculateCurrentFormRules';

import 'tippy.js/dist/tippy.css';

interface ProjectTableProps {
  fullWidth: boolean;
  width: number;
  setFullWidth: Dispatch<SetStateAction<boolean>>;
}

type LocalDisplaySettings = Pick<FormViewSettings, 'hideEmptyCells' | 'hideReferencedValues'>;

const getDisplaySettingsStorageKey = (userId?: number, formId?: number) =>
  userId && formId ? `table-display-settings:${userId}:${formId}` : null;

export const ProjectTablePage: FC<ProjectTableProps> = memo(
  ({ fullWidth, width, setFullWidth }) => {
    const {
      tableSettings,
      isTableSettingsLoading,
      selectedProject,
      selectedForm,
      forms,
      setTableSettings,
    } = useContext(ProjectsContext);
    const {
      setSearch,
      tableFilters,
      setTableFilters,
      isEdit,
      selectedRowId,
      workflowRefreshToken,
      refreshTable,
    } = useContext(TableContext);
    const { user } = useContext(UserContext);
    const { handleSelectForm } = useGetForm();

    const [isInfoVisible, setIsInfoVisible] = useState(false);
    const [isOpenFormModal, setIsOpenFormModal] = useState(false);
    const [isAuditHistoryOpen, setIsAuditHistoryOpen] = useState(false);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isDisplaySettingsOpen, setIsDisplaySettingsOpen] = useState(false);
    const [isRulesRecalculationLoading, setIsRulesRecalculationLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [localDisplaySettings, setLocalDisplaySettings] = useState<LocalDisplaySettings>({
      hideEmptyCells: false,
      hideReferencedValues: false,
    });

    const canManage = canManageModel(user);
    const canTransition = canWriteData(user);
    const canWrite = canTransition && !tableSettings?.workflowReadOnly;
    const isDirectory = Boolean(tableSettings?.isDirectory);

    const workflowStatusNameMap: Record<string, string> = {
      draft: 'Черновик',
      review: 'Проверка',
      approved: 'Утверждено',
      rejected: 'Отклонено',
      disabled: 'Выключено',
    };

    const workflowEnabled = Boolean(
      isDirectory
        ? tableSettings?.directoryWorkflowSettings?.enabled
        : tableSettings?.workflowSettings?.enabled,
    );

    const workflowStatusCode =
      !isDirectory && workflowEnabled
        ? (
            tableSettings?.workflowCurrentStatus ||
            tableSettings?.workflowSettings?.initialStatus ||
            'draft'
          )?.toLowerCase()
        : 'disabled';

    const workflowStatusLabel = workflowStatusNameMap[workflowStatusCode] || workflowStatusCode;

    const handleSearchDebounced = useMemo(
      () => debounce((value: string) => setSearch(value), 500),
      [setSearch],
    );

    useEffect(() => {
      setSearchValue('');
      setIsDisplaySettingsOpen(false);
    }, [tableSettings?.id]);

    useEffect(() => {
      const storageKey = getDisplaySettingsStorageKey(user?.id, tableSettings?.id);
      if (!storageKey || typeof window === 'undefined') {
        setLocalDisplaySettings({
          hideEmptyCells: false,
          hideReferencedValues: false,
        });
        return;
      }

      try {
        const rawValue = window.localStorage.getItem(storageKey);
        if (!rawValue) {
          setLocalDisplaySettings({
            hideEmptyCells: false,
            hideReferencedValues: false,
          });
          return;
        }

        const parsedValue = JSON.parse(rawValue) as LocalDisplaySettings;
        setLocalDisplaySettings({
          hideEmptyCells: Boolean(parsedValue?.hideEmptyCells),
          hideReferencedValues: Boolean(parsedValue?.hideReferencedValues),
        });
      } catch {
        setLocalDisplaySettings({
          hideEmptyCells: false,
          hideReferencedValues: false,
        });
      }
    }, [tableSettings?.id, user?.id]);

    useEffect(() => {
      const storageKey = getDisplaySettingsStorageKey(user?.id, tableSettings?.id);
      if (!storageKey || typeof window === 'undefined') {
        return;
      }

      window.localStorage.setItem(storageKey, JSON.stringify(localDisplaySettings));
    }, [localDisplaySettings, tableSettings?.id, user?.id]);

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setSearchValue(nextValue);
      handleSearchDebounced(nextValue);
    };

    const handleDisplaySettingChange =
      (key: keyof LocalDisplaySettings) => (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        setLocalDisplaySettings(prevState => ({
          ...prevState,
          [key]: nextValue,
        }));
      };

    const fieldLabels = useMemo(() => {
      const map = new Map<string, string>();
      (tableSettings?.formStructure || []).forEach(field => {
        map.set(field.columnName, field.columnLabel || field.columnName);
      });
      return map;
    }, [tableSettings?.formStructure]);

    const activeFilterLabels = useMemo(() => {
      const formatValue = (value: WsFilterCondition['value']) => {
        if (Array.isArray(value)) {
          return value.map(item => String(item ?? 'null')).join(', ');
        }
        if (value === undefined || value === null) {
          return '';
        }
        return String(value);
      };

      return tableFilters.map(filter => {
        const fieldLabel = fieldLabels.get(filter.field) || filter.field;
        if (filter.op === 'isnull') {
          return `${fieldLabel}: пусто`;
        }
        if (filter.op === 'notnull') {
          return `${fieldLabel}: не пусто`;
        }
        return `${fieldLabel} ${filter.op} ${formatValue(filter.value)}`.trim();
      });
    }, [fieldLabels, tableFilters]);

    const effectiveTableSettings = useMemo(() => {
      if (!tableSettings) {
        return null;
      }

      return {
        ...tableSettings,
        viewSettings: {
          ...(tableSettings.viewSettings || {}),
          hideEmptyCells:
            localDisplaySettings.hideEmptyCells ?? tableSettings.viewSettings?.hideEmptyCells,
          hideReferencedValues:
            localDisplaySettings.hideReferencedValues ??
            tableSettings.viewSettings?.hideReferencedValues,
        },
      };
    }, [localDisplaySettings, tableSettings]);

    const handleWorkflowUpdated = (workflowData: GetFormWorkflowRes) => {
      if (!tableSettings) {
        return;
      }
      setTableSettings({
        ...tableSettings,
        workflowSettings: workflowData.workflowSettings || tableSettings.workflowSettings,
        workflowCurrentStatus: workflowData.currentStatus,
        workflowReadOnly: workflowData.readOnly,
      });
    };

    const name = `${selectedProject?.project_name || ''}/${tableSettings?.alias || ''}`;
    const instanceForms = useMemo(() => forms.filter(form => !form.is_directory), [forms]);
    const currentInstanceIndex = useMemo(
      () => instanceForms.findIndex(form => form.id === tableSettings?.id),
      [instanceForms, tableSettings?.id],
    );
    const shareableLink = useMemo(() => {
      if (!tableSettings?.id || typeof window === 'undefined') {
        return '';
      }

      const query = new URLSearchParams();
      if (selectedProject?.id) {
        query.set('projectId', String(selectedProject.id));
      }
      query.set('formId', String(tableSettings.id));

      const alias = (tableSettings.alias || selectedForm?.form_name || '').trim();
      const path = alias ? `${routesEnum.TABLE}/${encodeURIComponent(alias)}` : routesEnum.TABLE;
      const queryString = query.toString();
      const baseUrl = buildAbsoluteAppUrl(path);

      return `${baseUrl}${queryString ? `?${queryString}` : ''}`;
    }, [selectedForm?.form_name, selectedProject?.id, tableSettings?.alias, tableSettings?.id]);
    const hasPrevInstance = currentInstanceIndex > 0;
    const hasNextInstance =
      currentInstanceIndex >= 0 && currentInstanceIndex < instanceForms.length - 1;

    const handleNavigateInstance = async (step: -1 | 1) => {
      if (currentInstanceIndex < 0) {
        return;
      }
      const targetForm = instanceForms[currentInstanceIndex + step];
      if (!targetForm) {
        return;
      }
      await handleSelectForm(targetForm);
    };

    const handleCurrentFormRulesRecalculation = async () => {
      if (isRulesRecalculationLoading || !tableSettings?.id) {
        return;
      }

      const confirmed = window.confirm(
        'Запустить пересчёт бизнес-правил только для текущей формы?',
      );
      if (!confirmed) {
        return;
      }

      setIsRulesRecalculationLoading(true);
      try {
        const isRecalculated = await recalculateCurrentFormRules(tableSettings.id);
        if (isRecalculated) {
          refreshTable();
        }
      } finally {
        setIsRulesRecalculationLoading(false);
      }
    };

    const handleCopyLink = async () => {
      if (!shareableLink) {
        setError('Не удалось сформировать ссылку на форму');
        return;
      }

      try {
        if (!navigator.clipboard?.writeText) {
          window.prompt('Скопируйте ссылку на форму', shareableLink);
          setWarning('Автокопирование недоступно, ссылка показана вручную');
          return;
        }

        await navigator.clipboard.writeText(shareableLink);
        setSuccess('Ссылка на форму скопирована');
      } catch {
        window.prompt('Скопируйте ссылку на форму', shareableLink);
        setWarning('Не удалось скопировать автоматически, ссылка показана вручную');
      }
    };

    const workflowBadge = isDirectory
      ? `Бизнес-процесс справочника${workflowEnabled ? ' настроен по значениям' : ' не включён'}`
      : `Бизнес-процесс: ${workflowStatusLabel}${
          workflowEnabled && tableSettings?.workflowReadOnly ? ' (только чтение)' : ''
        }`;

    return (
      <>
        <div className="relative flex h-full w-full flex-col px-2.5 py-5">
          <HeadItems fullWidth={fullWidth} setFullWidth={setFullWidth} />

          {!tableSettings && !isTableSettingsLoading && (
            <div className="relative flex h-full w-full flex-col items-center justify-center">
              {selectedProject ? (
                !isTableSettingsLoading ? (
                  forms?.length ? (
                    <div className="font-gothampro-500 text-lg">Форма не выбрана</div>
                  ) : (
                    <>
                      <div className="font-gothampro-500 mb-10 text-lg">
                        В настоящий момент в проекте нет актуальных таблиц.
                      </div>
                      {canManage && (
                        <Button
                          variant="green"
                          classNames="w-40"
                          onClick={() => setIsOpenFormModal(true)}
                        >
                          Создать
                        </Button>
                      )}
                    </>
                  )
                ) : (
                  <Loader classNames="w-40 h-40" />
                )
              ) : (
                <div className="font-gothampro-500 text-lg">Проект не выбран</div>
              )}
            </div>
          )}

          {tableSettings && (
            <>
              <div className="relative mb-6 flex flex-shrink-0 items-center pl-16">
                <ToolTip label={name} checkWidth>
                  <div className="font-gothampro-500 max-w-1/2 ml-4 flex truncate text-lg">
                    {name}
                  </div>
                </ToolTip>

                {instanceForms.length > 1 && !isDirectory && (
                  <div className="ml-4 flex items-center">
                    <button
                      className="rounded border border-grey-1000 px-2 py-1 text-xs disabled:cursor-default disabled:opacity-40"
                      disabled={!hasPrevInstance}
                      onClick={() => handleNavigateInstance(-1)}
                    >
                      Назад
                    </button>
                    <div className="mx-2 text-xs text-grey-400">
                      {`${currentInstanceIndex + 1}/${instanceForms.length}`}
                    </div>
                    <button
                      className="rounded border border-grey-1000 px-2 py-1 text-xs disabled:cursor-default disabled:opacity-40"
                      disabled={!hasNextInstance}
                      onClick={() => handleNavigateInstance(1)}
                    >
                      Вперёд
                    </button>
                  </div>
                )}

                <div className="ml-4 rounded bg-blue-rgba-100 px-3 py-1 text-xs text-blue-1500">
                  {workflowBadge}
                </div>

                {canManage && (
                  <Button
                    classNames="ml-4 w-56"
                    variant="violetOutline"
                    size="small"
                    onClick={handleCurrentFormRulesRecalculation}
                    disabled={isRulesRecalculationLoading}
                  >
                    Пересчитать правила
                  </Button>
                )}

                <Button
                  classNames="ml-4 w-40"
                  variant="violetOutline"
                  size="small"
                  onClick={handleCopyLink}
                >
                  Скопировать ссылку
                </Button>

                {!isDirectory && tableSettings?.id && (
                  <Button
                    classNames="ml-4 w-44"
                    variant="violetOutline"
                    size="small"
                    onClick={() => setIsAuditHistoryOpen(true)}
                  >
                    Журнал Аудита
                  </Button>
                )}

                <InfoCircleIcon
                  className="ml-2.5 h-6 w-6 cursor-pointer fill-violet-1300"
                  onClick={() => setIsInfoVisible(true)}
                />

                {isInfoVisible && (
                  <div className="absolute left-20 top-0 z-10 w-[38rem]">
                    <TableInfo onClose={() => setIsInfoVisible(false)} />
                  </div>
                )}
              </div>

              <div className="mb-4 flex flex-shrink-0 items-center">
                <div className="mr-5 flex h-11 w-2/3 items-center rounded bg-white pl-4 pr-2.5">
                  <input
                    className="w-full text-sm"
                    placeholder='Поиск или фильтр: where "направление"=1 and "сотрудник"="КАА"'
                    value={searchValue}
                    onChange={handleSearch}
                  />
                  <LoupeIcon className="ml-auto h-4 w-4 min-w-[1rem] fill-grey-800" />
                </div>

                <Button
                  variant={tableFilters.length ? 'violet' : 'violetOutline'}
                  size="small"
                  classNames="mr-3 flex w-40 items-center justify-center gap-2"
                  onClick={() => setIsFiltersModalOpen(true)}
                >
                  <FilterIcon className="h-4 w-4 fill-current" />
                  {`Фильтры${tableFilters.length ? ` (${tableFilters.length})` : ''}`}
                </Button>

                <div className="relative mr-3">
                  <Button
                    variant={
                      localDisplaySettings.hideEmptyCells ||
                      localDisplaySettings.hideReferencedValues
                        ? 'violet'
                        : 'violetOutline'
                    }
                    size="small"
                    classNames="w-32"
                    onClick={() => setIsDisplaySettingsOpen(prevState => !prevState)}
                  >
                    Вид
                  </Button>

                  {isDisplaySettingsOpen && (
                    <div className="absolute left-0 top-10 z-20 w-64 rounded border border-grey-1000 bg-white p-4 shadow-lg">
                      <div className="font-gothampro-500 mb-3 text-xs text-blue-1300">
                        Личные настройки отображения
                      </div>

                      <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-blue-1300">
                        <input
                          type="checkbox"
                          checked={Boolean(localDisplaySettings.hideEmptyCells)}
                          onChange={handleDisplaySettingChange('hideEmptyCells')}
                        />
                        Скрывать пустые ячейки
                      </label>

                      <label className="flex cursor-pointer items-center gap-2 text-xs text-blue-1300">
                        <input
                          type="checkbox"
                          checked={Boolean(localDisplaySettings.hideReferencedValues)}
                          onChange={handleDisplaySettingChange('hideReferencedValues')}
                        />
                        Скрывать ссылочные значения
                      </label>
                    </div>
                  )}
                </div>

                {tableFilters.length > 0 && (
                  <Button
                    variant="greyLight"
                    size="small"
                    classNames="mr-4 w-32"
                    onClick={() => setTableFilters([])}
                  >
                    Сбросить
                  </Button>
                )}

                {!isDirectory && workflowEnabled && tableSettings?.workflowReadOnly && (
                  <div className="font-gothampro-400 mr-4 text-xs text-red-400">
                    Форма доступна только для чтения в текущем статусе бизнес-процесса
                  </div>
                )}

                {canWrite && isEdit && <TableButtons />}
              </div>

              {activeFilterLabels.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {activeFilterLabels.map((label, index) => (
                    <div
                      key={`${label}-${index}`}
                      className="rounded-full bg-blue-rgba-100 px-3 py-1 text-xs text-blue-1500"
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}

              {isDirectory ? (
                <DirectoryWorkflowPanel
                  tableSettings={tableSettings}
                  selectedValueId={selectedRowId}
                  refreshToken={workflowRefreshToken}
                  canTransition={canTransition}
                  userRole={user?.role}
                />
              ) : (
                <FormWorkflowPanel
                  tableSettings={tableSettings}
                  userRole={user?.role}
                  userId={user?.id}
                  onWorkflowUpdated={handleWorkflowUpdated}
                />
              )}

              <div className="flex-1">
                <Table
                  tableSettings={effectiveTableSettings || tableSettings}
                  height="calc(100dvh - 186px)"
                  width={fullWidth ? '100%' : `calc(100dvw - ${width + 20}px)`}
                  setFullWidth={setFullWidth}
                />
              </div>
            </>
          )}
        </div>

        {isOpenFormModal && <CreateFormModal onClose={() => setIsOpenFormModal(false)} />}
        {isAuditHistoryOpen && tableSettings && (
          <FormAuditHistoryModal
            onClose={() => setIsAuditHistoryOpen(false)}
            tableId={tableSettings.id}
            formLabel={name}
          />
        )}
        {isFiltersModalOpen && tableSettings && (
          <TableFiltersModal
            formStructure={tableSettings.formStructure}
            value={tableFilters}
            onApply={setTableFilters}
            onClose={() => setIsFiltersModalOpen(false)}
          />
        )}
      </>
    );
  },
);
